import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiDownload, FiX } from "react-icons/fi";
import useAuth from "../../../hooks/useAuth";
import { medicineService } from "../../../services/medicine.service";
import salesmanService from "../../../services/salesman.service";
import SalesmanFormModal, { EMPTY_SALESMAN_FORM } from "../salesmen/SalesmanFormModal";
import PaginationBar from "../../../components/common/PaginationBar";
import { actsAsAgencyAdmin } from "../../../utils/permissions";
import "../../../styles/bankDetails.css";
import "../../../styles/users.css";
import "../../../styles/pagination.css";
import "../../../styles/downloadSampleExcelButton.css";
import "../../../styles/codeBankNbfc.css";
const PAGE_SIZE = 20;

const EMPTY_FORM = {
  medicineName: "",
  brandName: "",
  genericName: "",
  category: "",
  strength: "",
  dosageForm: "",
  manufacturer: "",
  batchNumber: "",
  manufacturingDate: "",
  expiryDate: "",
  purchaseDate: "",
  salesmanId: "",
  salesmanName: "",
  salesmanMobile: "",
  salesmanCompany: "",
  purchasePrice: "",
  sellingPrice: "",
  mrp: "",
  availableStock: "",
  stockQuantity: "",
  unit: "",
  branchName: "",
  description: "",
};

const DOSAGE_FORMS = ["Tablet", "Capsule", "Syrup", "Injection", "Cream", "Drops", "Inhaler"];
const UNITS = ["Tablet", "Strip", "Bottle", "Vial", "Tube", "Box", "ml"];

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "instock", label: "Available Stock" },
  { value: "low", label: "Low Stock" },
  { value: "expiring", label: "Expiring Soon" },
  { value: "expired", label: "Expired" },
  { value: "out", label: "Out of Stock" },
];

function formFromRow(row) {
  return {
    medicineName: row.medicineName || "",
    brandName: row.brandName || "",
    genericName: row.genericName || "",
    category: row.category || "",
    strength: row.strength || "",
    dosageForm: row.dosageForm || "",
    manufacturer: row.manufacturer || "",
    batchNumber: row.batchNumber || "",
    manufacturingDate: row.manufacturingDate || "",
    expiryDate: row.expiryDate || "",
    purchaseDate: row.purchaseDate || "",
    salesmanId: row.salesmanId || "",
    salesmanName: row.salesmanName || "",
    salesmanMobile: row.salesmanMobile || "",
    salesmanCompany: row.salesmanCompany || "",
    purchasePrice: row.purchasePrice ?? "",
    sellingPrice: row.sellingPrice ?? "",
    mrp: row.mrp ?? "",
    availableStock: row.availableStock ?? "",
    stockQuantity: "",
    unit: row.unit || "",
    branchName: row.branchName || "",
    description: row.description || "",
  };
}

function payloadFromForm(form) {
  return {
    medicineName: form.medicineName,
    brandName: form.brandName,
    genericName: form.genericName,
    category: form.category,
    strength: form.strength,
    dosageForm: form.dosageForm,
    manufacturer: form.manufacturer,
    batchNumber: form.batchNumber,
    manufacturingDate: form.manufacturingDate,
    expiryDate: form.expiryDate,
    purchaseDate: form.purchaseDate,
    salesmanId: form.salesmanId,
    salesmanName: form.salesmanName,
    salesmanMobile: form.salesmanMobile,
    salesmanCompany: form.salesmanCompany,
    purchasePrice: form.purchasePrice,
    sellingPrice: form.sellingPrice,
    mrp: form.mrp,
    availableStock: form.availableStock,
    unit: form.unit,
    branchName: form.branchName,
    description: form.description,
  };
}

export default function MedicineManagement() {
  const { auth } = useAuth();
  const permissions = Array.isArray(auth?.user?.permissions) ? auth.user.permissions : [];
  const canWrite = actsAsAgencyAdmin(auth) || permissions.includes("upload_records.view");
  const canSeePurchase = auth?.user?.role === "REPO_ADMIN";

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [branches, setBranches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [branch, setBranch] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [notice, setNotice] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [formError, setFormError] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [closeAsk, setCloseAsk] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);
  const [salesmanOptions, setSalesmanOptions] = useState([]);
  const [salesmanQuery, setSalesmanQuery] = useState("");
  const [salesmanModalOpen, setSalesmanModalOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const loadRows = useCallback(async () => {
    if (!auth?.token) {
      setLoading(false);
      setLoadError("Sign in again to load medicine records.");
      return;
    }
    setLoading(true);
    setLoadError("");
    try {
      const res = await medicineService.list(auth.token, {
        q: query,
        branch,
        category,
        status,
        page,
        limit: PAGE_SIZE,
      });
      setRows(Array.isArray(res?.data) ? res.data : []);
      setTotal(Number(res?.total) || 0);
      setTotalPages(Number(res?.totalPages) || 0);
      setBranches(Array.isArray(res?.branches) ? res.branches : []);
      setCategories(Array.isArray(res?.categories) ? res.categories : []);
    } catch (err) {
      setRows([]);
      setTotal(0);
      setTotalPages(0);
      setLoadError(err?.response?.data?.message || "Failed to load medicine records.");
    } finally {
      setLoading(false);
    }
  }, [auth?.token, query, branch, category, status, page]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const loadSalesmanOptions = useCallback(async () => {
    try {
      const res = await salesmanService.options();
      setSalesmanOptions(res?.data?.data?.records || []);
    } catch {
      setSalesmanOptions([]);
    }
  }, []);

  useEffect(() => {
    if (formOpen) loadSalesmanOptions();
  }, [formOpen, loadSalesmanOptions]);

  const applySalesman = (row) => {
    if (!row) {
      setForm((prev) => ({
        ...prev,
        salesmanId: "",
        salesmanName: "",
        salesmanMobile: "",
        salesmanCompany: "",
      }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      salesmanId: row.id,
      salesmanName: row.salesmanName || "",
      salesmanMobile: row.mobile || "",
      salesmanCompany: row.distributorName || "",
    }));
  };

  const openCreate = () => {
    setSelected(null);
    setForm(EMPTY_FORM);
    setFormError("");
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setSelected(row);
    setForm(formFromRow(row));
    setFormError("");
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setSelected(null);
    setForm(EMPTY_FORM);
    setFormError("");
  };

  const clearForm = () => {
    setForm(selected ? formFromRow(selected) : EMPTY_FORM);
    setFormError("");
  };

  const formDirty = useMemo(() => {
    const baseline = selected ? formFromRow(selected) : EMPTY_FORM;
    return Object.keys(EMPTY_FORM).some(
      (key) => String(form[key] ?? "") !== String(baseline[key] ?? "")
    );
  }, [form, selected]);

  const uploadDirty = Boolean(uploadFile || preview);

  const requestCloseForm = () => {
    if (saving) return;
    if (formDirty) {
      setCloseAsk("form");
      return;
    }
    closeForm();
  };

  const resetUpload = () => {
    setUploadFile(null);
    setUploadError("");
    setUploadProgress(0);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const closeUpload = () => {
    if (uploading) return;
    setUploadOpen(false);
    resetUpload();
  };

  const requestCloseUpload = () => {
    if (uploading) return;
    if (uploadDirty) {
      setCloseAsk("upload");
      return;
    }
    closeUpload();
  };

  const confirmDiscard = () => {
    const target = closeAsk;
    setCloseAsk("");
    if (target === "form") closeForm();
    if (target === "upload") closeUpload();
  };

  useEffect(() => {
    if (!formOpen && !uploadOpen) return undefined;
    const onKey = (event) => {
      if (event.key !== "Escape" || saving || uploading || closeAsk) return;
      if (uploadOpen) requestCloseUpload();
      else requestCloseForm();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const takeExcelFile = (file) => {
    setUploadError("");
    setPreview(null);
    setUploadProgress(0);
    if (!file) return;
    const name = file.name.toLowerCase();
    if (!name.endsWith(".xlsx") && !name.endsWith(".xls")) {
      setUploadError("Only .xlsx and .xls files are accepted.");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setUploadError("Excel file must be 8 MB or smaller.");
      return;
    }
    setUploadFile(file);
  };

  const exportRecords = async () => {
    setExporting(true);
    setLoadError("");
    try {
      const blob = await medicineService.exportRecords(auth.token, {
        q: query,
        branch,
        category,
        status,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "FastSearch-medicine-records.xlsx";
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setLoadError(err?.response?.data?.message || "Could not download medicine records.");
    } finally {
      setExporting(false);
    }
  };

  const downloadSample = async () => {
    setUploadError("");
    try {
      const blob = await medicineService.downloadSample(auth.token);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "FastSearch-medicine-sample.xlsx";
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setUploadError(err?.response?.data?.message || "Could not download the sample Excel.");
    }
  };

  const runPreview = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadError("");
    setUploadProgress(0);
    try {
      const res = await medicineService.previewImport(uploadFile, auth.token, (event) => {
        if (event.total) setUploadProgress(Math.round((event.loaded / event.total) * 100));
      });
      setPreview(res);
      setUploadProgress(100);
    } catch (err) {
      setPreview(null);
      setUploadError(err?.response?.data?.message || err.message || "Could not read the Excel file.");
    } finally {
      setUploading(false);
    }
  };

  const confirmImport = async () => {
    if (!uploadFile || !preview?.summary?.validRows) return;
    setUploading(true);
    setUploadError("");
    try {
      const res = await medicineService.commitImport(uploadFile, auth.token, (event) => {
        if (event.total) setUploadProgress(Math.round((event.loaded / event.total) * 100));
      });
      setNotice(res?.message || "Excel import finished.");
      setUploadOpen(false);
      resetUpload();
      setPage(1);
      await loadRows();
    } catch (err) {
      setUploadError(err?.response?.data?.message || err.message || "Import failed. No rows were saved.");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setFormError("");
    setSaving(true);
    try {
      const res = await medicineService.create(payloadFromForm(form), auth.token);
      setNotice(res?.message || "Medicine batch saved.");
      setFormOpen(false);
      setSelected(null);
      setForm(EMPTY_FORM);
      setPage(1);
      await loadRows();
    } catch (err) {
      setFormError(err?.response?.data?.message || err.message || "Failed to save medicine.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!selected) return;
    setFormError("");
    setSaving(true);
    try {
      const res = await medicineService.updateBatch(
        selected.medicineId,
        selected._id,
        payloadFromForm(form),
        auth.token
      );
      setNotice(res?.message || "Medicine batch updated.");
      setFormOpen(false);
      setSelected(null);
      await loadRows();
    } catch (err) {
      setFormError(err?.response?.data?.message || err.message || "Failed to update medicine.");
    } finally {
      setSaving(false);
    }
  };

  const handleStock = async (type) => {
    if (!selected) return;
    setFormError("");
    setSaving(true);
    try {
      const res = await medicineService.moveStock(
        selected.medicineId,
        selected._id,
        { type, quantity: form.stockQuantity },
        auth.token
      );
      setNotice(res?.message || "Stock updated.");
      const next = res?.data;
      if (next) {
        setSelected(next);
        setForm(formFromRow(next));
      }
      await loadRows();
    } catch (err) {
      setFormError(err?.response?.data?.message || err.message || "Failed to update stock.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setSaving(true);
    setLoadError("");
    try {
      const res = await medicineService.removeBatch(
        pendingDelete.medicineId,
        pendingDelete._id,
        auth.token
      );
      setNotice(res?.message || "Medicine batch deleted.");
      setPendingDelete(null);
      if (selected && String(selected._id) === String(pendingDelete._id)) closeForm();
      await loadRows();
    } catch (err) {
      setLoadError(err?.response?.data?.message || "Failed to delete medicine batch.");
      setPendingDelete(null);
    } finally {
      setSaving(false);
    }
  };

  const startIndex = useMemo(() => (page - 1) * PAGE_SIZE, [page]);
  const salesmanSelectOptions = useMemo(() => {
    const list = [...salesmanOptions];
    if (form.salesmanId && !list.some((item) => String(item.id) === String(form.salesmanId))) {
      list.unshift({
        id: form.salesmanId,
        salesmanName: form.salesmanName || "Saved salesman",
        mobile: form.salesmanMobile,
        distributorName: form.salesmanCompany,
      });
    }
    return list;
  }, [salesmanOptions, form.salesmanId, form.salesmanName, form.salesmanMobile, form.salesmanCompany]);

  return (
    <div className="bd-page">
      <div className="bd-header">
        <div className="users-actions">
          <div>
            <h1>Medicine Management</h1>
            <p>Manage medicine records, batches, stock and branch-wise inventory.</p>
          </div>
          {canWrite ? (
            <div className="users-actions-buttons">
              <button type="button" className="primary-page-btn" onClick={openCreate}>
                + Add Medicine
              </button>
              <button type="button" className="secondary-page-btn" onClick={() => setUploadOpen(true)}>
                Upload Excel
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {notice ? <p className="bd-feedback bd-success">{notice}</p> : null}

      <div className="bd-form-card">
        <div className="bd-form-grid">
          <label className="bd-field">
            <span className="bd-label">Search</span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Medicine, brand, generic, batch, manufacturer or salesman"
            />
          </label>
          <label className="bd-field">
            <span className="bd-label">Branch</span>
            <select
              value={branch}
              onChange={(event) => {
                setBranch(event.target.value);
                setPage(1);
              }}
            >
              <option value="">All branches</option>
              {branches.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
          <label className="bd-field">
            <span className="bd-label">Category</span>
            <select
              value={category}
              onChange={(event) => {
                setCategory(event.target.value);
                setPage(1);
              }}
            >
              <option value="">All categories</option>
              {categories.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
          <label className="bd-field">
            <span className="bd-label">Status</span>
            <select
              value={status}
              onChange={(event) => {
                setStatus(event.target.value);
                setPage(1);
              }}
            >
              {STATUS_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      <div className="bd-table-card">
        {loadError ? <p className="bd-feedback bd-error">{loadError}</p> : null}
        <div className="users-actions users-actions--with-export">
          <h2>Medicine batches</h2>
          <button
            type="button"
            className="btn-table-excel"
            disabled={loading || exporting}
            onClick={exportRecords}
          >
            <FiDownload aria-hidden />
            <span>{exporting ? "Preparing file…" : "Download Excel"}</span>
          </button>
        </div>
        {loading && rows.length === 0 ? (
          <p className="bd-muted">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="bd-muted">No medicine records match this search.</p>
        ) : (
          <div className="company-table-wrap bd-excel-sheet">
            <table className="users-table excel-grid-table bd-excel-table">
              <thead>
                <tr>
                  <th>S.No.</th>
                  <th>Medicine Name</th>
                  <th>Brand Name</th>
                  <th>Generic Name</th>
                  <th>Batch Number</th>
                  <th>Manufacturer</th>
                  <th>MFD</th>
                  <th>EXP</th>
                  <th>Purchase Date</th>
                  <th>Salesman Name</th>
                  <th>Salesman Mobile No.</th>
                  {canSeePurchase ? <th>Purchase Price</th> : null}
                  <th>MRP</th>
                  <th>Selling Price</th>
                  <th>Available Stock</th>
                  <th>Unit</th>
                  <th>Branch Name</th>
                  <th>Status</th>
                  {canWrite ? <th>Actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={row._id}>
                    <td>{startIndex + index + 1}</td>
                    <td>{row.medicineName}</td>
                    <td>{row.brandName || "—"}</td>
                    <td>{row.genericName || "—"}</td>
                    <td>{row.batchNumber}</td>
                    <td>{row.manufacturer || "—"}</td>
                    <td>{row.manufacturingDate || "—"}</td>
                    <td>{row.expiryDate || "—"}</td>
                    <td>{row.purchaseDate || "—"}</td>
                    <td>{row.salesmanName || "—"}</td>
                    <td>{row.salesmanMobile || "—"}</td>
                    {canSeePurchase ? <td>{row.purchasePrice ?? "—"}</td> : null}
                    <td>{row.mrp}</td>
                    <td>{row.sellingPrice}</td>
                    <td>{row.availableStock}</td>
                    <td>{row.unit || "—"}</td>
                    <td>{row.branchName}</td>
                    <td className="bd-col-status">
                      <span
                        className={`bd-status-badge ${
                          row.status === "Expired" || row.status === "Out of Stock"
                            ? "bd-status-badge--upload"
                            : "bd-status-badge--uploaded"
                        }`}
                      >
                        {row.status || "Active"}
                      </span>
                    </td>
                    {canWrite ? (
                      <td className="bd-col-actions">
                        <div className="bd-row-actions">
                          <button
                            type="button"
                            className="bd-btn bd-btn-secondary bd-btn-compact"
                            onClick={() => openEdit(row)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="bd-btn bd-btn-danger bd-btn-compact"
                            onClick={() => setPendingDelete(row)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <PaginationBar
          page={page}
          totalPages={totalPages}
          total={total}
          loading={loading}
          onPageChange={setPage}
        />
      </div>

      {formOpen ? (
        <div className="modal-overlay bd-upload-modal-overlay" onClick={requestCloseForm} role="presentation">
          <div
            className="modal-box bd-upload-modal mm-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="medicine-form-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mm-sheet__head">
              <div>
                <h3 id="medicine-form-title">{selected ? "Edit Medicine" : "Add Medicine"}</h3>
                <p className="modal-subtitle">
                  {selected
                    ? "Update this batch. Other batches of the medicine stay unchanged."
                    : "Save creates the medicine and its first batch."}
                </p>
              </div>
              <button type="button" className="mm-sheet__close" onClick={requestCloseForm} aria-label="Close">
                <FiX aria-hidden />
              </button>
            </div>
            <div className="mm-sheet__body">
            <form className="bd-form-grid" onSubmit={handleSave}>
              <Field label="Medicine Name *" value={form.medicineName} disabled={saving} onChange={(value) => setField("medicineName", value)} placeholder="Enter medicine name" />
              <Field label="Brand Name" value={form.brandName} disabled={saving} onChange={(value) => setField("brandName", value)} placeholder="Enter brand name" />
              <Field label="Generic Name" value={form.genericName} disabled={saving} onChange={(value) => setField("genericName", value)} placeholder="Enter generic name" />
              <Field label="Category" value={form.category} disabled={saving} onChange={(value) => setField("category", value)} placeholder="Enter category" />
              <Field label="Strength" value={form.strength} disabled={saving} onChange={(value) => setField("strength", value)} placeholder="Enter strength" />
              <label className="bd-field">
                <span className="bd-label">Dosage Form</span>
                <input list="mm-dosage-forms" value={form.dosageForm} disabled={saving} placeholder="Enter dosage form" onChange={(event) => setField("dosageForm", event.target.value)} />
                <datalist id="mm-dosage-forms">
                  {DOSAGE_FORMS.map((item) => (
                    <option key={item} value={item} />
                  ))}
                </datalist>
              </label>
              <Field label="Manufacturer" value={form.manufacturer} disabled={saving} onChange={(value) => setField("manufacturer", value)} placeholder="Enter manufacturer" />
              <Field label="Batch Number *" value={form.batchNumber} disabled={saving} onChange={(value) => setField("batchNumber", value)} placeholder="Enter batch number" />
              <Field label="Manufacturing Date" type="date" value={form.manufacturingDate} disabled={saving} onChange={(value) => setField("manufacturingDate", value)} />
              <Field label="Expiry Date *" type="date" value={form.expiryDate} disabled={saving} onChange={(value) => setField("expiryDate", value)} />
              <Field label="Purchase Date" type="date" value={form.purchaseDate} disabled={saving} onChange={(value) => setField("purchaseDate", value)} />
              <label className="bd-field bd-field-full">
                <span className="bd-label">Select Salesman</span>
                <div className="mm-salesman-pick">
                  <input
                    value={salesmanQuery}
                    disabled={saving}
                    placeholder="Search salesman name or mobile"
                    onChange={(event) => setSalesmanQuery(event.target.value)}
                  />
                  <div className="mm-salesman-pick__row">
                    <select
                      value={form.salesmanId}
                      disabled={saving}
                      onChange={(event) => {
                        const next = salesmanSelectOptions.find((item) => String(item.id) === event.target.value);
                        applySalesman(next || null);
                      }}
                    >
                      <option value="">Select salesman</option>
                      {salesmanSelectOptions
                        .filter((item) => {
                          const q = salesmanQuery.trim().toLowerCase();
                          if (!q) return true;
                          return [item.salesmanName, item.mobile, item.distributorName]
                            .some((value) => String(value || "").toLowerCase().includes(q));
                        })
                        .map((item) => (
                          <option key={item.id} value={item.id}>
                            {item.salesmanName} — {item.mobile}
                            {item.distributorName ? ` (${item.distributorName})` : ""}
                          </option>
                        ))}
                    </select>
                    {canWrite ? (
                      <button
                        type="button"
                        className="mm-salesman-pick__add"
                        disabled={saving}
                        onClick={() => setSalesmanModalOpen(true)}
                      >
                        + Add New Salesman
                      </button>
                    ) : null}
                  </div>
                </div>
              </label>
              <Field label="Salesman Name" value={form.salesmanName} disabled placeholder="Filled from selected salesman" onChange={() => {}} />
              <Field label="Salesman Mobile No." value={form.salesmanMobile} disabled placeholder="Filled from selected salesman" onChange={() => {}} />
              <Field label="Company / Distributor Name" value={form.salesmanCompany} disabled placeholder="Filled from selected salesman" onChange={() => {}} />
              {canSeePurchase ? (
                <Field label="Purchase Price" type="number" value={form.purchasePrice} disabled={saving} onChange={(value) => setField("purchasePrice", value)} placeholder="Enter purchase price" />
              ) : null}
              <Field label="Selling Price" type="number" value={form.sellingPrice} disabled={saving} onChange={(value) => setField("sellingPrice", value)} placeholder="Enter selling price" />
              <Field label="MRP" type="number" value={form.mrp} disabled={saving} onChange={(value) => setField("mrp", value)} placeholder="Enter MRP" />
              <Field label="Available Stock *" type="number" value={form.availableStock} disabled={saving} onChange={(value) => setField("availableStock", value)} placeholder="Enter available stock" />
              <label className="bd-field">
                <span className="bd-label">Unit</span>
                <input list="mm-units" value={form.unit} disabled={saving} placeholder="Enter unit" onChange={(event) => setField("unit", event.target.value)} />
                <datalist id="mm-units">
                  {UNITS.map((item) => (
                    <option key={item} value={item} />
                  ))}
                </datalist>
              </label>
              <Field label="Branch Name *" value={form.branchName} disabled={saving} onChange={(value) => setField("branchName", value)} placeholder="Enter branch name" />
              {selected ? (
                <Field label="Stock quantity" type="number" value={form.stockQuantity} disabled={saving} onChange={(value) => setField("stockQuantity", value)} placeholder="Quantity for stock in / out" />
              ) : null}
              <label className="bd-field bd-field-full">
                <span className="bd-label">Description</span>
                <input value={form.description} disabled={saving} placeholder="Enter description" onChange={(event) => setField("description", event.target.value)} />
              </label>
              {formError ? <p className="bd-feedback bd-error">{formError}</p> : null}
              <div className="bd-form-actions">
                <button type="button" className="bd-btn bd-btn-secondary" disabled={saving} onClick={clearForm}>
                  Clear
                </button>
                {selected ? (
                  <>
                    <button type="button" className="bd-btn bd-btn-secondary" disabled={saving} onClick={() => handleStock("OUT")}>
                      Stock Out
                    </button>
                    <button type="button" className="bd-btn bd-btn-secondary" disabled={saving} onClick={() => handleStock("IN")}>
                      Stock In
                    </button>
                    <button type="button" className="bd-btn bd-btn-primary" disabled={saving} onClick={handleUpdate}>
                      {saving ? "Saving…" : "Update"}
                    </button>
                  </>
                ) : (
                  <button type="submit" className="bd-btn bd-btn-primary" disabled={saving}>
                    {saving ? "Saving…" : "Save"}
                  </button>
                )}
              </div>
            </form>
            </div>
          </div>
        </div>
      ) : null}

      {uploadOpen ? (
        <div className="modal-overlay bd-upload-modal-overlay" onClick={requestCloseUpload} role="presentation">
          <div
            className="modal-box bd-upload-modal mm-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="medicine-upload-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mm-sheet__head">
              <div>
                <h3 id="medicine-upload-title">Bulk Medicine Upload</h3>
                <p className="modal-subtitle">Upload multiple medicine records and batches using an Excel file.</p>
              </div>
              <button type="button" className="mm-sheet__close" onClick={requestCloseUpload} aria-label="Close">
                <FiX aria-hidden />
              </button>
            </div>
            <div className="mm-sheet__body">
              <div className="sample-excel-banner">
                <div className="sample-excel-banner__content">
                  <p className="sample-excel-banner__title">Excel column layout</p>
                  <p className="sample-excel-banner__desc">
                    Download the sample first. Keep the same headers, then fill your medicine batches.
                  </p>
                </div>
                <button
                  type="button"
                  className="sample-excel-btn sample-excel-btn--solid"
                  onClick={downloadSample}
                  disabled={uploading}
                >
                  <span className="sample-excel-btn__label">Download Sample Excel</span>
                  <FiDownload className="sample-excel-btn__arrow" aria-hidden />
                </button>
              </div>

              <div
                className="mm-drop"
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  takeExcelFile(event.dataTransfer.files?.[0]);
                }}
              >
                <p className="bd-muted">Drag and drop an .xlsx or .xls file here, or choose a file.</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  hidden
                  onChange={(event) => takeExcelFile(event.target.files?.[0])}
                />
                <button
                  type="button"
                  className="bd-btn bd-btn-secondary bd-btn-compact"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  Choose Excel file
                </button>
                {uploadFile ? (
                  <p className="mm-file-name">
                    {uploadFile.name} · {uploadFile.size < 1024 * 1024
                      ? `${Math.max(1, Math.round(uploadFile.size / 1024))} KB`
                      : `${(uploadFile.size / (1024 * 1024)).toFixed(1)} MB`}
                    <button
                      type="button"
                      className="bd-btn bd-btn-secondary bd-btn-compact"
                      style={{ marginLeft: 8 }}
                      disabled={uploading}
                      onClick={resetUpload}
                    >
                      Remove
                    </button>
                  </p>
                ) : null}
              </div>
              {uploading ? <p className="bd-muted">Uploading… {uploadProgress}%</p> : null}
              {uploadError ? <p className="bd-feedback bd-error">{uploadError}</p> : null}
              {preview?.summary ? (
                <>
                  <div className="bd-form-grid">
                    <p className="bd-muted">Total Excel rows: {preview.summary.totalRows}</p>
                    <p className="bd-muted">Valid rows: {preview.summary.validRows}</p>
                    <p className="bd-muted">Invalid rows: {preview.summary.invalidRows}</p>
                    <p className="bd-muted">Empty rows skipped: {preview.summary.emptyRows || 0}</p>
                    <p className="bd-muted">New medicines: {preview.summary.newMedicines}</p>
                    <p className="bd-muted">Existing medicines: {preview.summary.existingMedicines}</p>
                    <p className="bd-muted">New batches: {preview.summary.newBatches}</p>
                    <p className="bd-muted">Duplicate batches: {preview.summary.duplicateBatches}</p>
                  </div>
                  {preview.errors?.length ? (
                    <div className="company-table-wrap bd-excel-sheet">
                      <table className="users-table excel-grid-table bd-excel-table">
                        <thead>
                          <tr>
                            <th>Excel Row</th>
                            <th>Medicine</th>
                            <th>Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.errors.map((error) => (
                            <tr key={`${error.row}-${error.message}`}>
                              <td>{error.row}</td>
                              <td>{error.medicine}</td>
                              <td>{error.message}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                  {preview.errorCount > preview.errors.length ? (
                    <p className="bd-muted">Showing the first {preview.errors.length} errors of {preview.errorCount}.</p>
                  ) : null}
                </>
              ) : null}
              <div className="bd-form-actions">
                <button type="button" className="bd-btn bd-btn-secondary" disabled={uploading} onClick={requestCloseUpload}>
                  Cancel
                </button>
                {!preview ? (
                  <button type="button" className="bd-btn bd-btn-primary" disabled={uploading || !uploadFile} onClick={runPreview}>
                    {uploading ? "Checking…" : "Upload Excel"}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="bd-btn bd-btn-primary"
                    disabled={uploading || !preview.summary.validRows}
                    onClick={confirmImport}
                  >
                    {uploading ? "Importing…" : "Confirm Import"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {closeAsk ? (
        <div className="modal-overlay" role="presentation">
          <div className="modal-box" role="dialog" aria-modal="true">
            <h3>Close without saving?</h3>
            <p className="modal-subtitle">This closes the window and does not save or delete anything.</p>
            <div className="modal-actions">
              <button type="button" className="secondary-page-btn" onClick={() => setCloseAsk("")}>
                Keep editing
              </button>
              <button type="button" className="primary-page-btn" onClick={confirmDiscard}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {salesmanModalOpen ? (
        <SalesmanFormModal
          initial={EMPTY_SALESMAN_FORM}
          onClose={() => setSalesmanModalOpen(false)}
          onSaved={async (record) => {
            setSalesmanModalOpen(false);
            await loadSalesmanOptions();
            if (record) applySalesman(record);
          }}
        />
      ) : null}

      {pendingDelete ? (
        <div className="modal-overlay" onClick={() => !saving && setPendingDelete(null)} role="presentation">
          <div className="modal-box" role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <h3>Delete batch</h3>
            <p className="modal-subtitle">
              Delete {pendingDelete.medicineName} batch {pendingDelete.batchNumber}
              {pendingDelete.branchName ? ` at ${pendingDelete.branchName}` : ""}? Other batches of this medicine stay saved.
            </p>
            <div className="modal-actions">
              <button type="button" className="secondary-page-btn" disabled={saving} onClick={() => setPendingDelete(null)}>
                Cancel
              </button>
              <button type="button" className="primary-page-btn modal-btn-danger" disabled={saving} onClick={confirmDelete}>
                {saving ? "Deleting…" : "Delete"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", disabled }) {
  return (
    <label className="bd-field">
      <span className="bd-label">{label}</span>
      <input
        type={type}
        min={type === "number" ? "0" : undefined}
        step={type === "number" ? "0.01" : undefined}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}
